/**
 * Ouverture des documents comptables (rapports Z, rapports e-commerce,
 * inventaires).
 *
 * Ces PDF étaient déposés sur S3 en lecture publique, sous un nom entièrement
 * prévisible (`Z-<date>-<séquence>`) : n'importe qui pouvait télécharger le
 * livre de caisse en devinant une date, sans laisser de trace dans les journaux
 * du site (audit du 15/09/2026). Ils sont désormais privés et relayés par
 * l'API, qui vérifie le jeton et l'appartenance au tenant.
 *
 * Un `<a href>` ne peut pas porter d'en-tête `Authorization` : il faut donc
 * récupérer le fichier puis l'ouvrir depuis un blob.
 */
import { logger } from './logger';

function baseApi(): string {
  return (import.meta as any).env?.VITE_API_URL || '/api';
}

/** Même résolution que l'ApiClient : le tenant du personnel est un objet JSON. */
function tenantId(): string | null {
  try {
    const brut = localStorage.getItem('tenant');
    return brut ? JSON.parse(brut)?.id ?? null : null;
  } catch {
    return null;
  }
}

/**
 * Récupère le document et l'ouvre dans un nouvel onglet.
 *
 * ⚠️ L'onglet est ouvert AVANT l'attente réseau : ouvert après, il serait
 * bloqué comme fenêtre surgissante par Safari et Chrome, car plus rattaché au
 * geste de l'utilisateur.
 */
/** Le document lui-même, relayé par l'API (jeton + tenant). */
export async function recupererDocumentPrive(url: string): Promise<Blob> {
  const token = localStorage.getItem('auth_token');
  const reponse = await fetch(
    `${baseApi()}/uploads/document-prive?url=${encodeURIComponent(url)}`,
    {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(tenantId() ? { 'x-tenant-id': tenantId() as string } : {}),
      },
    },
  );
  if (!reponse.ok) {
    throw new Error(`HTTP ${reponse.status}`);
  }
  return reponse.blob();
}

export async function ouvrirDocumentPrive(url: string): Promise<void> {
  if (!url) return;

  const onglet = window.open('', '_blank');
  try {
    const blob = await recupererDocumentPrive(url);
    const lien = URL.createObjectURL(blob);
    if (onglet) {
      onglet.location.href = lien;
    } else {
      // Fenêtre surgissante refusée : on retombe sur un téléchargement.
      const a = document.createElement('a');
      a.href = lien;
      a.download = url.split('/').pop() || 'document.pdf';
      a.click();
    }
    // Laisser le temps à l'onglet de charger avant de révoquer.
    setTimeout(() => URL.revokeObjectURL(lien), 60_000);
  } catch (e) {
    onglet?.close();
    logger.error('Ouverture du document refusée', e);
    alert("Impossible d'ouvrir ce document. Vérifiez que vous êtes bien connecté.");
  }
}
